import { createRow, MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import { useSelector } from "react-redux";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { useParams } from "react-router";

const FeuB3Consumers = forwardRef(({ b3FuelConsumerValues, refetchMonitoringData, b3FuelConsumerData,vesselMstId }, ref) => {
	const RowActions = {
		Edit: 0,
		Add: 1,
	};
	const [activeAction, setactiveAction] = useState(-1);
	const [fuelData, setFuelData] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deletingRowIndex, setDeletingRowIndex] = useState();


	const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
	};

	const fuelTypeOptions = useSelector(
		(state) => state.myFleet.mrvDropdowns?.allfueltypes || []
	);

	const reindexRefNumbers = list =>
		list.map((row, i) => ({
			...row,
			emissionSourceReferenceNumber: String(i + 1),
		}));
	const getNextRefNumber = useCallback(
		() => String(fuelData.length + 1),
		[fuelData],
	);
	const asNullable = (n) =>
		n == null || Number(n) === 0 ? null : Number(n);
	useEffect(() => {
		if (Array.isArray(b3FuelConsumerData)) {
			const transformedData = b3FuelConsumerData?.map((item) => {
				const technicalDesc = item?.technicalDescription || {};
				return {
					emissionSourceReferenceNumber: item?.emissionSourceReferenceNumber || '',
					emissionSourceName: item?.emissionSourceName || '',
					emissionSourceType: item?.emissionSourceType || '',
					emissionSourceClass: item?.emissionSourceClass || '',
					emissionSourceTypeCode: technicalDesc.emissionSourceTypeCode || '',
					technicalDescription: {

						identificationNumber: technicalDesc.identificationNumber || '',
						ratingPower: asNullable(technicalDesc.ratingPower),
						specFuelOilCons: asNullable(technicalDesc.specFuelOilCons),
						yearOfInstallation: asNullable(technicalDesc.yearOfInstallation),
						otherTechnicalDescription: technicalDesc.otherTechnicalDescription || '',
						otherFuelType: technicalDesc.otherFuelType || '',
					},

					fuelTypesUsed: Array.isArray(item.fuelTypesUsed)
						? item.fuelTypesUsed
						: typeof item.fuelTypesUsed === 'string'
							? item.fuelTypesUsed.split(',')?.map((v) => v.trim())
							: [],
				};
			});

			setFuelData(reindexRefNumbers(transformedData));
		}
	}, [b3FuelConsumerData]);

	const handleFuelActions = (table, row, action) => {
		if (activeAction !== -1) return;

		if (action === RowActions.Add) {
			const defaultRow = createRow(table, {
				emissionSourceReferenceNumber: getNextRefNumber(),
				emissionSourceName: '',
				emissionSourceType: '',
				emissionSourceClass: '',
				fuelTypesUsed: [],

				technicalDescription: {
					identificationNumber: '',
					ratingPower: null,
					specFuelOilCons: null,
					yearOfInstallation: null,
					otherTechnicalDescription: '',
					otherFuelType: '',
					emissionSourceTypeCode: '',
				},
			});

			table.setCreatingRow(defaultRow);
			setactiveAction(action);
			return;
		}

		if (action === RowActions.Edit) {
			table.setEditingRow(row);
			setactiveAction(action);
		}
	};

	const resetActiveRow = () => {
		setactiveAction(-1);
		setValidationErrors({});
	};

	const validateRequired = (value) => {
		if (typeof value === "string") {
			return !!value?.length;
		} else if (typeof value === "number") {
			return value >= 0;
		}
		return false;
	};

	const getEmissionsSource = () => {
		return (
			b3FuelConsumerValues?.emissionSourceClasses?.map((type) => ({
				label: type.name,
				value: type.code,
			})) || []
		);
	};
	const getEmissionsSourceType = () => {
		return (
			b3FuelConsumerValues?.emissionSourceTypes?.map((type) => ({
				label: type.name,
				value: type.code,
			})) || []
		);
	};
	const getFuelType = () => {
		return (
			b3FuelConsumerValues?.fuelTypes?.map((type) => ({
				label: type.name,
				value: type.code,
			})) || []
		);
	};

	const emissionSourceTypeOptions = useMemo(getEmissionsSourceType, [b3FuelConsumerValues]);
	const emissionSourceClassOptions = useMemo(getEmissionsSource, [b3FuelConsumerValues]);
	const fuelTypeOptionsLabels = useMemo(getFuelType, [b3FuelConsumerValues]);

	const typeLabelByCode = useMemo(
		() => Object.fromEntries(emissionSourceTypeOptions.map(o => [o.value, o.label])),
		[emissionSourceTypeOptions],
	);

	const classLabelByCode = useMemo(
		() => Object.fromEntries(emissionSourceClassOptions.map(o => [o.value, o.label])),
		[emissionSourceClassOptions],
	);

	const fuelLabelByCode = useMemo(
		() => Object.fromEntries(fuelTypeOptionsLabels.map(o => [o.value, o.label])),
		[fuelTypeOptionsLabels],
	);


	const validateFuel = (rowValues, mode, currentRowIndex, fuelData) => {
		const errors = {};

		if (!validateRequired(rowValues.emissionSourceType)) {
			errors.emissionSourceType = 'Field is required';
		}
		if (!rowValues.fuelTypesUsed || rowValues.fuelTypesUsed.length === 0) {
			errors.fuelTypesUsed = 'Field is required';
		}
		const rawYear = rowValues['technicalDescription.yearOfInstallation'];
		if (rawYear !== '' && rawYear !== undefined && rawYear !== null) {
			const yr = Number(rawYear);
			if (Number.isNaN(yr) || yr < 1900) {
				errors.yearOfInstallation = 'Value should not be less than 1900';
			}
		}
		const isDuplicate = fuelData?.some(
			(d, idx) =>
				idx !== currentRowIndex &&
				d.emissionSourceReferenceNumber === rowValues.emissionSourceReferenceNumber,
		);
		if (isDuplicate) {
			errors.emissionSourceReferenceNumber = 'Reference number already used';
		}
		return errors;
	};
	const toNumberOrZero = (v) =>
		v === null || v === undefined || v === "" ? 0 : +v;

	const sanitizeTechnicalDescription = (item) => ({
		...item.technicalDescription,
		ratingPower: toNumberOrZero(item.technicalDescription?.ratingPower),
		specFuelOilCons: toNumberOrZero(item.technicalDescription?.specFuelOilCons),
		yearOfInstallation: toNumberOrZero(item.technicalDescription?.yearOfInstallation),
	});

	const prepareFinalPayload = (data) =>
		reindexRefNumbers(data).map((item) => ({
			...item,
			b3EmissionSourceId: 0,
			technicalDescription: sanitizeTechnicalDescription(item),
		}));


	useImperativeHandle(ref, () => ({
		async submitAndCheck() {
			const { creatingRow, editingRow } = table.getState();
			if (!creatingRow && !editingRow) return true;
			const mode = creatingRow ? "add" : "edit";
			const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
			let isDirty = false;
			if (editingRow && editedValues) {
				isDirty = Object.keys(editedValues).some(
					(key) => editedValues[key] !== editingRow.original[key]
				);
			}
			const validationRes = validateFuel(editedValues, mode, {});
			const hasErrors = Object.values(validationRes).some((err) => err);

			if (hasErrors) {
				setValidationErrors(validationRes);
				emitToasterMsg(
					"warning",
					"Validation Error",
					"Please correct the highlighted fields before proceeding."
				);
				return false;
			}

			// Autosave on valid & dirty
			if (creatingRow) {
				await handleCreateEmissions({ values: editedValues, table }, true);
				return true;
			} else if (editingRow && isDirty) {
				await handleUpdateEmissions({ values: editedValues, table, row: editingRow }, true);
				return true;
			}

			return true;
		}
	}));

	const handleCreateEmissions = async ({ values, table }, isAuto = false) => {
		const newValidationErrors = validateFuel(values, "add", {});
		if (Object.values(newValidationErrors)?.some((error) => error)) {
			setValidationErrors(newValidationErrors);
			return;
		}
		setValidationErrors({});

		const newRow = {
			b3EmissionSourceId: 0,
			emissionSourceReferenceNumber: values?.emissionSourceReferenceNumber,
			emissionSourceName: values?.emissionSourceName,
			emissionSourceType: values?.emissionSourceType,
			emissionSourceClass: values?.emissionSourceClass,
			fuelTypesUsed: values?.fuelTypesUsed || [],
			technicalDescription: {
				emissionSourceTypeCode: values?.emissionSourceType,
				identificationNumber: values["technicalDescription.identificationNumber"],
				ratingPower: +values["technicalDescription.ratingPower"] || null,
				specFuelOilCons: +values["technicalDescription.specFuelOilCons"] || null,
				yearOfInstallation: +values["technicalDescription.yearOfInstallation"] || null,
				otherTechnicalDescription: values["technicalDescription.otherTechnicalDescription"],
				otherFuelType: values.FuelType || ""
			}
		};
		const payload = [...fuelData, newRow];
		const finalPayload = prepareFinalPayload(payload);

		try {
			const b3Emissionsource = await callApi({
				method: "POST",
				url: API_ROUTES.B3(vesselMstId),
				data: finalPayload,
			});
			if (b3Emissionsource?.isSuccess) {
				emitToasterMsg("success", "Table B.3", isAuto ? "Fuel Consumers autosaved successfully" : "Fuel Consumers added successfully");
				table.setCreatingRow(null);
				setFuelData(finalPayload);
				setactiveAction(-1);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}

			}
			else {
				emitToasterMsg("error", "Table B.3", b3Emissionsource?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table B.3", error.errorMessage);
		}
	};

	const handleUpdateEmissions = async ({ values, table, row }, isAuto = false) => {
		const newValidationErrors = validateFuel(values, "edit", {});
		if (Object.values(newValidationErrors)?.some((error) => error)) {
			setValidationErrors(newValidationErrors);
			return;
		}
		setValidationErrors({});
		const updated = fuelData.map((item, index) => {
			if (index === row.index) {
				return {
					...item,
					emissionSourceReferenceNumber: values?.emissionSourceReferenceNumber,
					emissionSourceName: values?.emissionSourceName,
					emissionSourceType: values?.emissionSourceType,
					emissionSourceClass: values?.emissionSourceClass,
					fuelTypesUsed: values?.fuelTypesUsed || [],
					technicalDescription: {
						emissionSourceTypeCode: values?.emissionSourceType,
						identificationNumber: values["technicalDescription.identificationNumber"],
						ratingPower: +values["technicalDescription.ratingPower"] || null,
						specFuelOilCons: +values["technicalDescription.specFuelOilCons"] || null,
						yearOfInstallation: +values["technicalDescription.yearOfInstallation"] || null,
						otherTechnicalDescription: values["technicalDescription.otherTechnicalDescription"],
						otherFuelType: values.FuelType || ""
					}
				};
			}
			return item;
		});
		const finalPayload = prepareFinalPayload(updated);

		try {
			const b3Emissionsource = await callApi({
				method: "POST",
				url: API_ROUTES.B3(vesselMstId),
				data: finalPayload,
			});
			if (b3Emissionsource?.isSuccess) {
				emitToasterMsg("success", "Table B.3", isAuto ? "Fuel Consumers autosaved successfully" : "Fuel Consumers updated successfully");
				table.setEditingRow(null);
				setactiveAction(-1);
				setFuelData(finalPayload);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			}
			else {
				emitToasterMsg("error", "Table B.3", b3Emissionsource?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table B.3", error.errorMessage);
		}
	};

	const handleDeleteEmissions = async (rowIndex) => {
		const updated = fuelData.filter((_, i) => i !== rowIndex);
		const finalPayload = prepareFinalPayload(updated);
		try {
			const b3Emissionsource = await callApi({
				method: "POST",
				url: API_ROUTES.B3(vesselMstId),
				data: finalPayload,
			});
			if (b3Emissionsource?.isSuccess) {
				emitToasterMsg("success", "Table B.3", "Fuel Consumers deleted successfully");
				setactiveAction(-1);
				setFuelData(finalPayload);
				setConfirmDelete(false);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			}
			else {
				emitToasterMsg("error", "Table B.3", b3Emissionsource?.errorMessage);
				setConfirmDelete(false);
			}
		} catch (error) {
			emitToasterMsg("error", "Table B.3", error.errorMessage);
		}
	};

	const handleDecimal12Digits = useCallback((e) => {
		let v = e.target.value;

		// keep digits and dot
		v = v.replace(/[^0-9.]/g, "");

		// only the first dot survives
		const dot = v.indexOf(".");
		if (dot !== -1) {
			v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
		}

		// max 12 digits (dot doesn’t count)
		const digits = v.replace(".", "");
		if (digits.length > 12) {
			const allowed = digits.slice(0, 12);

			if (dot !== -1) {
				const intLen = Math.min(allowed.length, dot);   // digits before dot
				v = allowed.slice(0, intLen) + "." + allowed.slice(intLen);
			} else {
				v = allowed;
			}
		}

		e.target.value = v;
	}, []);

	const columns = useMemo(
		() => [
			{
				accessorKey: "emissionSourceReferenceNumber",
				header: "N° Fuel consumer unit",
				enableEditing: false,
				muiTableHeadCellProps: {
					sx: {
						borderBottom: 'none',
						borderRight: '1px solid lightgray',
						verticalAlign: "middle"
					},
				},

			},
			{
				accessorKey: "emissionSourceName",
				header: "Name of fuel consumer unit",
				muiTableHeadCellProps: {
					sx: {
						borderBottom: 'none',
						borderRight: '1px solid lightgray',
						verticalAlign: "middle"
					},
				},
				muiEditTextFieldProps: {
					inputProps: {
						maxLength: 255,
					},
				},
			},

			{
				accessorKey: "emissionSourceType",
				header: "Fuel consumer type",
				editVariant: 'select',
				editSelectOptions: getEmissionsSourceType,
				Cell: ({ cell }) => typeLabelByCode[cell.getValue()] ?? cell.getValue(),
				size: 220,
				muiEditTextFieldProps: ({ row }) => ({
					required: true,
					error: !!validationErrors?.emissionSourceType,
					helperText: validationErrors?.emissionSourceType,
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							emissionSourceType: undefined,
						}),
					SelectProps: {
						MenuProps: {
							disableScrollLock: true,
							PaperProps: {
								sx: {
									maxHeight: 250,
									boxShadow: 3,
									maxWidth: 180,
									overflowY: "auto",
									'&::-webkit-scrollbar': {
										width: "8px"
									},
									'&::-webkit-scrollbar-thumb': {
										backgroundColor: 'darkgray',
										borderRadius: '3px',
									},
									'&::-webkit-scrollbar-track': {
										backgroundColor: '#f0f0f0',
									},
								},
							},
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							transformOrigin: {
								vertical: "top",
								horizontal: "left",
							},
							getContentAnchorEl: null,
						}
					}
				}),
				muiTableHeadCellProps: {
					sx: {
						borderBottom: 'none',
						borderRight: '1px solid lightgray',
						verticalAlign: "middle"
					},
				},
			},

			{
				accessorKey: "emissionSourceClass",
				header: "Fuel consumer unit class",
				editVariant: 'select',
				editSelectOptions: getEmissionsSource,
				Cell: ({ cell }) => classLabelByCode[cell.getValue()] ?? cell.getValue(),
				size: 310,
				muiTableHeadCellProps: {
					sx: {
						borderBottom: 'none',
						borderRight: '1px solid lightgray',
						verticalAlign: "middle"
					},
				},
				muiEditTextFieldProps: () => {
					return {
						SelectProps: {
							MenuProps: {
								disableScrollLock: true,
								PaperProps: {
									sx: {
										maxHeight: 250,
										maxWidth: 280,
										boxShadow: 3,
										overflowY: 'auto',
										whiteSpace: 'nowrap',
										'&::-webkit-scrollbar': {
											height: '8px',
											width: "8px"
										},
										'&::-webkit-scrollbar-thumb': {
											backgroundColor: 'darkgray',
											borderRadius: '3px',
										},
										'&::-webkit-scrollbar-track': {
											backgroundColor: '#f0f0f0',
										},
									},
								},
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "left",
								},
								transformOrigin: {
									vertical: "top",
									horizontal: "left",
								},
								getContentAnchorEl: null,
							}
						}

					};
				},
			},
			{
				id: "technicalDescription",
				header: "Technical description of fuel consumer unit (performance/power, specific fuel oil consumption (SFOC), year of installation, identification number in case of multiple identical emission sources, etc.",
				muiTableHeadCellProps: {
					className: "grouped-header",
				},
				columns: [
					{
						accessorKey: "technicalDescription.identificationNumber",
						header: "Identification number",
						muiEditTextFieldProps: {
							inputProps: {
								maxLength: 255,
							},
						},
					},
					{
						accessorKey: "technicalDescription.ratingPower",
						header: "Rating power (KW)",
						muiEditTextFieldProps: {
							inputProps: {
								inputMode: "numeric",
								pattern: "\\d{0,12}(\\.\\d*)?",
								maxLength: 13,
								onInput: handleDecimal12Digits,
							},
						},
					},
					{
						accessorKey: "technicalDescription.specFuelOilCons",
						header: "Specific fuel oil consumption (g/KWh)",
						muiEditTextFieldProps: {
							inputProps: {
								inputMode: "numeric",
								pattern: "\\d{0,12}(\\.\\d*)?",
								maxLength: 13,
								onInput: handleDecimal12Digits,
							},
						},
					},
					{
						accessorKey: 'technicalDescription.yearOfInstallation',
						header: 'Year of installation',
						muiEditTextFieldProps: ({ row }) => ({
							type: 'number',
							error: !!validationErrors?.yearOfInstallation,
							helperText: validationErrors?.yearOfInstallation,

							inputProps: {
								inputMode: 'numeric',
								pattern: '\\d{0,4}',
								maxLength: 4,
								min: 1900,
							},

							onFocus: () =>
								setValidationErrors(prev => ({ ...prev, yearOfInstallation: undefined })),

							onInput: (e) => {
								e.target.value = e.target.value.replace(/\D/g, '').slice(0, 4);
							},
						}),
					},
					{
						accessorKey: "technicalDescription.otherTechnicalDescription",
						header: "Other technical description of emission source",
						muiEditTextFieldProps: {

							inputProps: {
								maxLength: 1000,
							},


						},
					}
				]
			},
			{
				accessorKey: "fuelTypesUsed",
				header: "(Potential) Fuel type(s) used",
				editVariant: 'select',
				editSelectOptions: fuelTypeOptionsLabels,
				Cell: ({ cell }) =>
					(cell.getValue() || [])
						.map(code => fuelLabelByCode[code] ?? code)
						.join(', '),
				size: 300,
				muiEditTextFieldProps: ({ cell, row }) => ({
					select: true,
					required: true,
					value: cell.getValue() || [],
					error: !!validationErrors?.fuelTypesUsed,
					helperText: validationErrors?.fuelTypesUsed,
					SelectProps: {
						multiple: true,
						renderValue: (selected) => {
							if (!selected || selected?.length === 0) return 'Select fuel type(s)';
							return (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected?.map((code) => {
										const label =
											fuelTypeOptions.find((opt) => opt.code === code)?.name || code;
										return (
											<Chip
												key={code}
												label={fuelLabelByCode[code] ?? code}
												onDelete={(e) => {
													e.stopPropagation();
													const newValue = selected.filter((v) => v !== code);
													row._valuesCache["fuelTypesUsed"] = newValue;
													setValidationErrors((prev) => ({
														...prev,
														fuelTypesUsed: undefined,
													}));
												}}
												onMouseDown={(e) => e.stopPropagation()}
											/>
										);
									})}
								</Box>
							);
						},
						MenuProps: {
							disableScrollLock: true,
							PaperProps: {
								sx: {
									textAlign: "center",
									maxHeight: 250,
									maxWidth: 280,
									boxShadow: 3,
									overflowY: 'auto',
									whiteSpace: 'nowrap',
									'&::-webkit-scrollbar': {
										height: '8px',
										width: "8px"
									},
									'&::-webkit-scrollbar-thumb': {
										backgroundColor: 'darkgray',
										borderRadius: '3px',
									},
									'&::-webkit-scrollbar-track': {
										backgroundColor: '#f0f0f0',
									},
								},
							},
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							transformOrigin: {
								vertical: "top",
								horizontal: "left",
							},
							getContentAnchorEl: null,
						}
					},
					onChange: (event) => {
						const value = event.target.value;
						row._valuesCache["fuelTypesUsed"] = value;
						setValidationErrors((prev) => ({
							...prev,
							fuelTypesUsed: undefined,
						}));
					},
				}),
				muiTableHeadCellProps: {
					sx: {
						borderBottom: 'none',
						borderRight: '1px solid lightgray',
						verticalAlign: "middle",
					},
				},
			}

		], [
		typeLabelByCode,
		classLabelByCode,
		fuelLabelByCode,
		validationErrors,
	]
	)

	const table = useMaterialReactTable({
		columns,
		data: fuelData || [],
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "520px",
			},
		},
		displayColumnDefOptions: {
			'mrt-row-actions': {
				muiTableHeadCellProps: {
					sx: {
						borderBottom: 'none',
						borderRight: '1px solid lightgray',
					},
				},
			},
		},
		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				verticalAlign: "top",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		columnOrder: [
			"emissionSource",
			"nameOfEmissionsSource",
			"typeOfEmissionsSource",
			"emissionsSourceClass",
			"techinal",
			"nameOfEmissionsSource"

		],
		getRowId: (row) => { },
		onCreatingRowSave: handleCreateEmissions,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		onEditingRowSave: handleUpdateEmissions,
		renderToolbarInternalActions: ({ table }) => <></>,
		renderRowActions: ({ row, table }) => {
			return (
				<Box sx={{ display: "flex", gap: "1rem" }}>
					<Tooltip title="Edit">
						<IconButton
							style={{ padding: "0px" }}
							onClick={() =>
								handleFuelActions(
									table,
									row,
									RowActions.Edit
								)
							}
						>
							<EditIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Delete">
						<IconButton
							style={{ padding: "0px" }}
							color="error"
							onClick={() => handleDeleteBtnClick(row?.index, true)}
						>
							<DeleteIcon />
						</IconButton>
					</Tooltip>
				</Box>
			);
		},
		renderTopToolbarCustomActions: ({ table }) => (
			<div className="mt-1">
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={() => {
						handleFuelActions(table, null, RowActions.Add);
					}}
					disabled={!!table.getState().creatingRow}
				>
					Add Fuel Consumer
				</Button>
			</div>
		),
	});

	return (
		<>
			<div className="fuel-registry-b3 mt-1">
				<MaterialReactTable table={table} />
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => { handleDeleteEmissions(deletingRowIndex) }}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this fuel consumer?</div>
				</Modal>
			</div>
		</>
	);
});

export default FeuB3Consumers;